import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { tap } from 'rxjs';

import { AccountSelectors, CurrentUserSelectors } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import type { JobBoardCodename } from '@core/job-boards-types';
import { JobItemsSelectors } from '@core/offers-data';
import type { OfferItem } from '@core/offers-types';
import { OfferStatus } from '@core/offers-types';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';
import {
  FirstJobPublishedDialogComponent,
  JobPublishedDialogComponent,
  // eslint-disable-next-line boundaries/element-types -- FIXME
} from '@recruitee/offers-smart-ui';

@Injectable({
  providedIn: 'root',
})
export class JobPublishedDialogFacade {
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  private autoPostings: Array<JobBoardCodename.GoogleForJobs | JobBoardCodename.Indeed> = [];

  constructor() {
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean, takeUntilDestroyed(this.destroyRef))
      .subscribe(company => {
        this.autoPostings = company.autoPostings;
      });
  }

  public open(jobId: number, isJobEditorContext = false): void {
    const offer = this.store.selectSignal(JobItemsSelectors.byId(jobId))();
    const currentAdmin = this.store.selectSignal(CurrentUserSelectors.data)();
    if (!offer) return;

    const isFirstJobPublished = currentAdmin?.featureDiscovery.firstJobPublishedUsed;

    if (!isFirstJobPublished) {
      this.openFirstJobPublishedDialog(offer);
    } else {
      this.openJobPublishedDialog(offer, isJobEditorContext);
    }
  }

  private openJobPublishedDialog(offer: OfferItem, isJobEditorContext: boolean): void {
    const status = <OfferStatus.Internal | OfferStatus.Published>offer.status;
    const title = offer.title;
    const department = offer.department || null;
    const locationIds = offer.locationIds || [];
    const workModel = {
      onSite: offer.onSite,
      remote: offer.remote,
      hybrid: offer.hybrid,
    };
    const priority = offer.priority || null;

    this.dialogService
      .openCustom(
        JobPublishedDialogComponent,
        { width: DialogWidth.Small },
        {
          status,
          title,
          department,
          locationIds,
          workModel,
          priority,
          autoPostings: this.autoPostings,
          isJobEditorContext,
        },
      )
      .afterClosed.pipe(
        filterBoolean,
        tap(() => this.redirect(status, offer.slug)),
      )
      .subscribe();
  }

  private openFirstJobPublishedDialog(offer: OfferItem): void {
    const status = <OfferStatus.Internal | OfferStatus.Published>offer.status;

    this.dialogService
      .openCustom(FirstJobPublishedDialogComponent, { width: DialogWidth.Small }, { status })
      .afterClosed.pipe(
        tap(() => {
          this.store.dispatch(
            FeatureDiscoveryActions.update({ payload: { firstJobPublishedUsed: true } }),
          );
        }),
        filterBoolean,
        tap(() => this.redirect(status, offer.slug)),
      )
      .subscribe();
  }

  private redirect(jobStatus: OfferStatus, jobSlug: string): void {
    this.router.navigate([{ outlets: { 'job-editor': null } }]).then(() => {
      if (jobStatus === OfferStatus.Published) {
        this.router.navigate(['offers', jobSlug, 'promote']);
      } else {
        this.router.navigate(['offers', jobSlug, 'pipeline']);
      }
    });
  }
}
